import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import '../styles/self-assessment.css';

const PHQ9_QUESTIONS = [
  'Little interest or pleasure in doing things.',
  'Feeling down, depressed, or hopeless.',
  'Trouble falling or staying asleep, or sleeping too much.',
  'Feeling tired or having little energy.',
  'Poor appetite or overeating.',
  'Feeling bad about yourself — or that you are a failure or have let yourself and your family down.',
  'Trouble concentrating on things, at work or otherwise.',
  'Moving or speaking so slowly that other people could have noticed? Or the opposite — being so fidgety or restless that you have been moving around more than usual.',
  'Thoughts that you would be better off dead, or of hurting yourself in some way.',
];

const GAD7_QUESTIONS = [
  'Feeling nervous, anxious, or on edge.',
  'Not being able to stop or control worrying.',
  'Worrying too much about different things.',
  'Trouble relaxing.',
  'Being so restless that it\'s hard to sit still.',
  'Becoming easily annoyed or irritable.',
  'Feeling afraid as if something awful might happen.',
];

const PHQ9_OPTIONS = [
  { value: 0, label: 'Not at all' },
  { value: 1, label: 'Several days' },
  { value: 2, label: 'More than half the days' },
  { value: 3, label: 'Nearly every day' },
];

const GAD7_OPTIONS = [
  { value: 0, label: 'Not at all' },
  { value: 1, label: 'Several days' },
  { value: 2, label: 'More than half the days' },
  { value: 3, label: 'Nearly every day' },
];

const DIFFICULTY_OPTIONS = [
  'Not difficult at all',
  'Somewhat difficult',
  'Very difficult',
  'Extremely difficult',
];

const phq9Severity = (score) => {
  if (score <= 4) return { band: 'Minimal', tone: 'good' };
  if (score <= 9) return { band: 'Mild', tone: 'mild' };
  if (score <= 14) return { band: 'Moderate', tone: 'moderate' };
  if (score <= 19) return { band: 'Moderately severe', tone: 'high' };
  return { band: 'Severe', tone: 'high' };
};

const gad7Severity = (score) => {
  if (score <= 4) return { band: 'Minimal', tone: 'good' };
  if (score <= 9) return { band: 'Mild', tone: 'mild' };
  if (score <= 14) return { band: 'Moderate', tone: 'moderate' };
  return { band: 'Severe', tone: 'high' };
};

const ScaleRow = ({ index, text, name, options, value, onChange, optionsClass }) => (
  <fieldset className="scale-row">
    <legend className="scale-row-q">
      <span className="scale-row-num">{index + 1}.</span> {text}
    </legend>
    <div className={`scale-row-options${optionsClass ? ` ${optionsClass}` : ''}`}>
      {options.map((opt) => {
        const id = `${name}-${opt.value}`;
        return (
          <label key={opt.value} htmlFor={id} className={`scale-opt ${value === opt.value ? 'is-selected' : ''}`}>
            <input
              id={id}
              type="radio"
              name={name}
              value={opt.value}
              checked={value === opt.value}
              onChange={() => onChange(opt.value)}
            />
            <span className="scale-opt-num">{opt.value}</span>
            <span className="scale-opt-label">{opt.label}</span>
          </label>
        );
      })}
    </div>
  </fieldset>
);

const PHQ_INSIGHTS = {
  Minimal: {
    summary: 'Your responses suggest minimal depressive symptoms right now.',
    steps: [
      'Keep up the routines and connections that are working for you.',
      'Re-screen in a few weeks if anything changes.',
    ],
  },
  Mild: {
    summary: 'You\'re showing mild depressive symptoms — worth keeping an eye on.',
    steps: [
      'Self-care, movement, and sleep can meaningfully shift mild symptoms.',
      'Re-screen in 2–4 weeks to see if things move.',
      'Lean on the people you trust.',
    ],
  },
  Moderate: {
    summary: 'Your score suggests moderate depression — a clinician can help.',
    steps: [
      'Consider booking a consultation in the next 1–2 weeks.',
      'Therapy, lifestyle support, or medication may all be options.',
      'Tell someone close to you how you\'re feeling.',
    ],
  },
  'Moderately severe': {
    summary: 'Symptoms in this range usually benefit from active treatment.',
    steps: [
      'Reach out to a mental health professional soon.',
      'Treatment can meaningfully reduce these symptoms.',
      'You don\'t need to navigate this alone.',
    ],
  },
  Severe: {
    summary: 'Your score indicates severe depression — please seek support.',
    steps: [
      'Connect with a clinician as soon as you can.',
      'If you feel unsafe, call Tele-MANAS 14416 (toll-free, 24×7).',
      'Effective help exists and you deserve it.',
    ],
  },
};

const PhqResultTile = ({ score, band, tone, difficulty }) => {
  const max = 27;
  const radius = 56;
  const circumference = 2 * Math.PI * radius;
  const progress = Math.min(Math.max(score / max, 0), 1);
  const dashOffset = circumference * (1 - progress);
  const insight = PHQ_INSIGHTS[band] ?? PHQ_INSIGHTS.Minimal;

  return (
    <article className={`result-tile result-tile-phq tone-${tone}`}>
      <header className="result-tile-head">
        <div className="result-tile-id">
          <span className="result-tile-tag">PHQ-9 · Depression</span>
          <h3 className="result-tile-title">Your screening result</h3>
        </div>
        <span className="result-tile-band">{band}</span>
      </header>

      <div className="result-tile-body result-tile-body-phq">
        <div className="result-tile-viz">
          <div className="phq-ring-wrap" aria-hidden="true">
            <svg className="phq-ring" viewBox="0 0 140 140">
              <circle className="phq-ring-track" cx="70" cy="70" r={radius} />
              <circle
                className="phq-ring-progress"
                cx="70"
                cy="70"
                r={radius}
                style={{
                  strokeDasharray: circumference,
                  strokeDashoffset: dashOffset,
                }}
              />
            </svg>
            <div className="phq-ring-center">
              <div className="phq-ring-score">{score}</div>
              <div className="phq-ring-max">of {max}</div>
            </div>
          </div>
          <span className="result-tile-vizlabel">Symptom score · past 2 weeks</span>
        </div>

        <div className="result-tile-insights">
          <div className="insight-block">
            <span className="insight-label">What this means</span>
            <p className="insight-summary">{insight.summary}</p>
          </div>
          <div className="insight-block">
            <span className="insight-label">Suggested next steps</span>
            <ul className="insight-steps">
              {insight.steps.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {difficulty && (
        <footer className="result-tile-foot">
          <span>Functional impact</span>
          <b>{difficulty}</b>
        </footer>
      )}
    </article>
  );
};

const GAD_INSIGHTS = {
  Minimal: {
    summary: 'Your responses suggest minimal anxiety symptoms right now.',
    steps: [
      'Keep practising what helps you stay grounded.',
      'Re-check if life or stress levels shift.',
    ],
  },
  Mild: {
    summary: 'You\'re showing mild anxiety — manageable, but worth attending to.',
    steps: [
      'Sleep, movement, and breathing practices can help.',
      'Notice which situations spike your worry.',
      'Re-screen in 2–4 weeks.',
    ],
  },
  Moderate: {
    summary: 'Your score suggests moderate anxiety — support can help.',
    steps: [
      'A clinician can help you build practical coping tools.',
      'CBT and similar approaches work well in this range.',
      'Limit caffeine and protect sleep where you can.',
    ],
  },
  Severe: {
    summary: 'Your score indicates severe anxiety — please reach out for support.',
    steps: [
      'Connect with a mental health professional soon.',
      'Treatment can meaningfully reduce symptoms.',
      'Call Tele-MANAS 14416 if you need someone right now.',
    ],
  },
};

const GAD_ZONES = [
  { key: 'good', label: 'Minimal', max: 4 },
  { key: 'mild', label: 'Mild', max: 9 },
  { key: 'moderate', label: 'Moderate', max: 14 },
  { key: 'high', label: 'Severe', max: 21 },
];

const GadResultTile = ({ score, band, tone, difficulty }) => {
  const max = 21;
  const markerPct = Math.min(Math.max((score / max) * 100, 0), 100);
  const insight = GAD_INSIGHTS[band] ?? GAD_INSIGHTS.Minimal;

  return (
    <article className={`result-tile result-tile-gad tone-${tone}`}>
      <header className="result-tile-head">
        <div className="result-tile-id">
          <span className="result-tile-tag">GAD-7 · Anxiety</span>
          <h3 className="result-tile-title">Your screening result</h3>
        </div>
        <span className="result-tile-band">{band}</span>
      </header>

      <div className="result-tile-body result-tile-body-gad">
        <div className="result-tile-viz">
          <div className="gad-score-block">
            <span className="gad-score-num">{score}</span>
            <span className="gad-score-max">of {max}</span>
          </div>
          <div className="gad-meter" aria-hidden="true">
            <div className="gad-meter-track">
              {GAD_ZONES.map((z) => (
                <span key={z.key} className={`gad-meter-zone zone-${z.key}`} />
              ))}
            </div>
            <span className="gad-meter-marker" style={{ left: `${markerPct}%` }}>
              <span className="gad-meter-dot" />
            </span>
            <div className="gad-meter-labels">
              {GAD_ZONES.map((z) => (
                <span key={z.key}>{z.label}</span>
              ))}
            </div>
          </div>
        </div>

        <div className="result-tile-insights">
          <div className="insight-block">
            <span className="insight-label">What this means</span>
            <p className="insight-summary">{insight.summary}</p>
          </div>
          <div className="insight-block">
            <span className="insight-label">Suggested next steps</span>
            <ul className="insight-steps">
              {insight.steps.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {difficulty && (
        <footer className="result-tile-foot">
          <span>Functional impact</span>
          <b>{difficulty}</b>
        </footer>
      )}
    </article>
  );
};

const DifficultyRow = ({ name, value, onChange }) => (
  <fieldset className="difficulty-row">
    <legend>
      If you have checked off any problems, how difficult have these problems made it for you to do
      your work, take care of things at home, or get along with other people?
    </legend>
    <div className="difficulty-options">
      {DIFFICULTY_OPTIONS.map((opt) => {
        const id = `${name}-${opt.replace(/\s+/g, '-').toLowerCase()}`;
        return (
          <label key={opt} htmlFor={id} className={`difficulty-opt ${value === opt ? 'is-selected' : ''}`}>
            <input
              id={id}
              type="radio"
              name={name}
              value={opt}
              checked={value === opt}
              onChange={() => onChange(opt)}
            />
            <span>{opt}</span>
          </label>
        );
      })}
    </div>
  </fieldset>
);

const AUDIT_QUESTIONS = [
  {
    text: 'How often do you have a drink containing alcohol?',
    options: [
      { value: 0, label: 'Never' },
      { value: 1, label: 'Monthly or less' },
      { value: 2, label: '2–4 times a month' },
      { value: 3, label: '2–3 times a week' },
      { value: 4, label: '4 or more times a week' },
    ],
  },
  {
    text: 'How many drinks containing alcohol do you have on a typical day when you are drinking?',
    options: [
      { value: 0, label: '0–2' },
      { value: 1, label: '3 or 4' },
      { value: 2, label: '5 or 6' },
      { value: 3, label: '7 to 9' },
      { value: 4, label: '10 or more' },
    ],
  },
  {
    text: 'How often do you have five or more drinks on one occasion?',
    options: [
      { value: 0, label: 'Never' },
      { value: 1, label: 'Less than monthly' },
      { value: 2, label: 'Monthly' },
      { value: 3, label: 'Weekly' },
      { value: 4, label: 'Daily or almost daily' },
    ],
  },
  {
    text: 'How often during the last year have you found that you were not able to stop drinking once you had started?',
    options: [
      { value: 0, label: 'Never' },
      { value: 1, label: 'Less than monthly' },
      { value: 2, label: 'Monthly' },
      { value: 3, label: 'Weekly' },
      { value: 4, label: 'Daily or almost daily' },
    ],
  },
  {
    text: 'How often during the last year have you failed to do what was normally expected of you because of drinking?',
    options: [
      { value: 0, label: 'Never' },
      { value: 1, label: 'Less than monthly' },
      { value: 2, label: 'Monthly' },
      { value: 3, label: 'Weekly' },
      { value: 4, label: 'Daily or almost daily' },
    ],
  },
  {
    text: 'How often in the last year have you needed a first drink in the morning to get yourself going after a heavy drinking session?',
    options: [
      { value: 0, label: 'Never' },
      { value: 1, label: 'Less than monthly' },
      { value: 2, label: 'Monthly' },
      { value: 3, label: 'Weekly' },
      { value: 4, label: 'Daily or almost daily' },
    ],
  },
  {
    text: 'How often during the last year have you had a feeling of guilt or remorse after drinking?',
    options: [
      { value: 0, label: 'Never' },
      { value: 1, label: 'Less than monthly' },
      { value: 2, label: 'Monthly' },
      { value: 3, label: 'Weekly' },
      { value: 4, label: 'Daily or almost daily' },
    ],
  },
  {
    text: 'How often during the last year have you been unable to remember what happened the night before because of your drinking?',
    options: [
      { value: 0, label: 'Never' },
      { value: 1, label: 'Less than monthly' },
      { value: 2, label: 'Monthly' },
      { value: 3, label: 'Weekly' },
      { value: 4, label: 'Daily or almost daily' },
    ],
  },
  {
    text: 'Have you or someone else been injured because of your drinking?',
    options: [
      { value: 0, label: 'No' },
      { value: 2, label: 'Yes, but not in the last year' },
      { value: 4, label: 'Yes, in the last year' },
    ],
  },
  {
    text: 'Has a relative, friend, doctor, or other health care worker been concerned about your drinking or suggested you cut down?',
    options: [
      { value: 0, label: 'No' },
      { value: 2, label: 'Yes, but not in the last year' },
      { value: 4, label: 'Yes, in the last year' },
    ],
  },
];

const AUDIT_TREATMENT_OPTIONS = ['Never', 'Currently', 'In the past'];

const auditSeverity = (score) => {
  if (score <= 3) return { band: 'Low risk', tone: 'good' };
  if (score <= 9) return { band: 'Risky', tone: 'mild' };
  if (score <= 13) return { band: 'Harmful', tone: 'moderate' };
  return { band: 'Severe', tone: 'high' };
};

const AUDIT_INSIGHTS = {
  'Low risk': {
    summary: 'Your drinking pattern appears to be low risk at this time.',
    steps: [
      'Continue drinking within safe limits.',
      'Re-screen periodically if habits change.',
    ],
  },
  Risky: {
    summary: 'Your drinking level is above low-risk and may affect your health over time.',
    steps: [
      'Consider speaking to your doctor about your drinking habits.',
      'Set personal limits and track your intake.',
      'Reducing frequency or quantity gradually can help.',
    ],
  },
  Harmful: {
    summary: 'Your drinking pattern is harmful and likely affecting your health and relationships.',
    steps: [
      'Talk to your doctor — counselling and structured support are available.',
      'Avoid drinking alone or as a way to cope with stress.',
      'A structured reduction plan with professional guidance can help.',
    ],
  },
  Severe: {
    summary: 'Your score indicates severe alcohol use — professional support is important.',
    steps: [
      'Seek medical advice promptly — abrupt cessation can be unsafe without guidance.',
      'Treatment options include counselling, medication, and structured programmes.',
      'You don\'t have to manage this alone.',
    ],
  },
};

const AUDIT_ZONES = [
  { key: 'good', label: 'Low risk', max: 3 },
  { key: 'mild', label: 'Risky', max: 9 },
  { key: 'moderate', label: 'Harmful', max: 13 },
  { key: 'high', label: 'Severe', max: 40 },
];

const AuditResultTile = ({ score, band, tone, treatment }) => {
  const max = 40;
  const markerPct = Math.min(Math.max((score / max) * 100, 0), 100);
  const insight = AUDIT_INSIGHTS[band] ?? AUDIT_INSIGHTS['Low risk'];

  return (
    <article className={`result-tile result-tile-audit tone-${tone}`}>
      <header className="result-tile-head">
        <div className="result-tile-id">
          <span className="result-tile-tag">AUDIT · Alcohol Use</span>
          <h3 className="result-tile-title">Your screening result</h3>
        </div>
        <span className="result-tile-band">{band}</span>
      </header>

      <div className="result-tile-body result-tile-body-gad">
        <div className="result-tile-viz">
          <div className="gad-score-block">
            <span className="gad-score-num">{score}</span>
            <span className="gad-score-max">of {max}</span>
          </div>
          <div className="gad-meter" aria-hidden="true">
            <div className="gad-meter-track">
              {AUDIT_ZONES.map((z) => (
                <span key={z.key} className={`gad-meter-zone zone-${z.key}`} />
              ))}
            </div>
            <span className="gad-meter-marker" style={{ left: `${markerPct}%` }}>
              <span className="gad-meter-dot" />
            </span>
            <div className="gad-meter-labels">
              {AUDIT_ZONES.map((z) => (
                <span key={z.key}>{z.label}</span>
              ))}
            </div>
          </div>
        </div>

        <div className="result-tile-insights">
          <div className="insight-block">
            <span className="insight-label">What this means</span>
            <p className="insight-summary">{insight.summary}</p>
          </div>
          <div className="insight-block">
            <span className="insight-label">Suggested next steps</span>
            <ul className="insight-steps">
              {insight.steps.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {treatment && (
        <footer className="result-tile-foot">
          <span>Prior treatment for alcohol</span>
          <b>{treatment}</b>
        </footer>
      )}
    </article>
  );
};

const FTND_QUESTIONS = [
  {
    text: 'How soon after you wake up do you smoke your first cigarette?',
    options: [
      { value: 3, label: 'Within 5 minutes' },
      { value: 2, label: '6–30 minutes' },
      { value: 1, label: '31–60 minutes' },
      { value: 0, label: 'After 60 minutes' },
    ],
  },
  {
    text: 'Do you find it difficult to refrain from smoking in places where it is forbidden (e.g. movie theatre, library, temple/church)?',
    options: [
      { value: 1, label: 'Yes' },
      { value: 0, label: 'No' },
    ],
  },
  {
    text: 'Which cigarette would you hate most to give up?',
    options: [
      { value: 1, label: 'The first one in the morning' },
      { value: 0, label: 'Any other' },
    ],
  },
  {
    text: 'How many cigarettes per day do you smoke?',
    options: [
      { value: 0, label: '10 or less' },
      { value: 1, label: '11–20' },
      { value: 2, label: '21–30' },
      { value: 3, label: '31 or more' },
    ],
  },
  {
    text: 'Do you smoke more frequently during the first hours after waking than during the rest of the day?',
    options: [
      { value: 1, label: 'Yes' },
      { value: 0, label: 'No' },
    ],
  },
  {
    text: 'Do you smoke if you are so ill that you are in bed most of the day?',
    options: [
      { value: 1, label: 'Yes' },
      { value: 0, label: 'No' },
    ],
  },
];

const ftndSeverity = (score) => {
  if (score <= 2) return { band: 'Low', tone: 'good' };
  if (score <= 4) return { band: 'Low to moderate', tone: 'mild' };
  if (score <= 7) return { band: 'Moderate', tone: 'moderate' };
  return { band: 'High', tone: 'high' };
};

const FTND_INSIGHTS = {
  Low: {
    summary: 'You show low nicotine dependence.',
    steps: [
      'Behavioural strategies alone may be enough to help you quit.',
      'Set a quit date and track your smoking patterns.',
      'Seek support if previous quit attempts have failed.',
    ],
  },
  'Low to moderate': {
    summary: 'You show low to moderate nicotine dependence.',
    steps: [
      'Consider speaking to a doctor about quit aids.',
      'Nicotine replacement therapy (NRT) can reduce cravings.',
      'Identifying your triggers helps manage urges.',
    ],
  },
  Moderate: {
    summary: 'Your dependence is moderate — treatment can significantly improve your chances of quitting.',
    steps: [
      'Talk to a doctor — NRT or prescription medication can help.',
      'Combining pharmacotherapy with behavioural support gives the best results.',
      'Plan ahead for withdrawal symptoms in the first 1–2 weeks.',
    ],
  },
  High: {
    summary: 'You show high nicotine dependence — medical support is strongly recommended.',
    steps: [
      'Consult a doctor before attempting to quit.',
      'Combination NRT or prescription medications have the highest success rates.',
      'Structured cessation programmes offer the best long-term outcomes.',
    ],
  },
};

const FtndResultTile = ({ score, band, tone }) => {
  const max = 10;
  const radius = 56;
  const circumference = 2 * Math.PI * radius;
  const progress = Math.min(Math.max(score / max, 0), 1);
  const dashOffset = circumference * (1 - progress);
  const insight = FTND_INSIGHTS[band] ?? FTND_INSIGHTS.Low;

  return (
    <article className={`result-tile result-tile-ftnd tone-${tone}`}>
      <header className="result-tile-head">
        <div className="result-tile-id">
          <span className="result-tile-tag">FTND · Nicotine Dependence</span>
          <h3 className="result-tile-title">Your screening result</h3>
        </div>
        <span className="result-tile-band">{band}</span>
      </header>

      <div className="result-tile-body result-tile-body-phq">
        <div className="result-tile-viz">
          <div className="phq-ring-wrap" aria-hidden="true">
            <svg className="phq-ring" viewBox="0 0 140 140">
              <circle className="phq-ring-track" cx="70" cy="70" r={radius} />
              <circle
                className="phq-ring-progress"
                cx="70"
                cy="70"
                r={radius}
                style={{
                  strokeDasharray: circumference,
                  strokeDashoffset: dashOffset,
                }}
              />
            </svg>
            <div className="phq-ring-center">
              <div className="phq-ring-score">{score}</div>
              <div className="phq-ring-max">of {max}</div>
            </div>
          </div>
          <span className="result-tile-vizlabel">Dependence score</span>
        </div>

        <div className="result-tile-insights">
          <div className="insight-block">
            <span className="insight-label">What this means</span>
            <p className="insight-summary">{insight.summary}</p>
          </div>
          <div className="insight-block">
            <span className="insight-label">Suggested next steps</span>
            <ul className="insight-steps">
              {insight.steps.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </article>
  );
};

const PSS_FORWARD_OPTIONS = [
  { value: 0, label: 'Never' },
  { value: 1, label: 'Almost never' },
  { value: 2, label: 'Sometimes' },
  { value: 3, label: 'Fairly often' },
  { value: 4, label: 'Very often' },
];

const PSS_REVERSE_OPTIONS = [
  { value: 4, label: 'Never' },
  { value: 3, label: 'Almost never' },
  { value: 2, label: 'Sometimes' },
  { value: 1, label: 'Fairly often' },
  { value: 0, label: 'Very often' },
];

const PSS_QUESTIONS = [
  { text: 'In the last month, how often have you been upset because of something that happened unexpectedly?', options: PSS_FORWARD_OPTIONS },
  { text: 'In the last month, how often have you felt you were unable to control the important things in your life?', options: PSS_FORWARD_OPTIONS },
  { text: 'In the last month, how often have you felt nervous and stressed?', options: PSS_FORWARD_OPTIONS },
  { text: 'In the last month, how often have you felt confident to handle your personal problems?', options: PSS_REVERSE_OPTIONS },
  { text: 'In the last month, how often have you felt that things were going your way?', options: PSS_REVERSE_OPTIONS },
  { text: 'In the last month, how often have you felt that you could not cope with all the things you had to do?', options: PSS_FORWARD_OPTIONS },
  { text: 'In the last month, how often have you been able to control irritations in your life?', options: PSS_REVERSE_OPTIONS },
  { text: 'In the last month, how often have you felt that you were on top of things?', options: PSS_REVERSE_OPTIONS },
  { text: 'In the last month, how often have you been angered because of things that happened that were outside of your control?', options: PSS_FORWARD_OPTIONS },
  { text: 'In the last month, how often have you felt difficulties were piling up so high that you could not overcome them?', options: PSS_FORWARD_OPTIONS },
];

const pssSeverity = (score) => {
  if (score <= 13) return { band: 'Low stress', tone: 'good' };
  if (score <= 26) return { band: 'Moderate stress', tone: 'moderate' };
  return { band: 'High stress', tone: 'high' };
};

const PSS_INSIGHTS = {
  'Low stress': {
    summary: 'Your perceived stress level is low.',
    steps: [
      'Keep up the habits and routines that are helping you stay grounded.',
      'Re-screen if your circumstances change significantly.',
    ],
  },
  'Moderate stress': {
    summary: 'Your perceived stress is moderate and may be affecting your daily functioning.',
    steps: [
      'Mindfulness, regular exercise, and sleep hygiene can meaningfully reduce stress.',
      'Identify the main stressors and consider what can be changed or delegated.',
      'Consider speaking to a counsellor if stress persists.',
    ],
  },
  'High stress': {
    summary: 'Your perceived stress is high — support is recommended.',
    steps: [
      'Reach out to a mental health professional for structured support.',
      'High chronic stress has physical and mental health consequences — it is worth taking seriously.',
      'Therapy, lifestyle changes, and social support can all make a meaningful difference.',
    ],
  },
};

const PSS_ZONES = [
  { key: 'good', label: 'Low', max: 13 },
  { key: 'moderate', label: 'Moderate', max: 26 },
  { key: 'high', label: 'High', max: 40 },
];

const PssResultTile = ({ score, band, tone }) => {
  const max = 40;
  const markerPct = Math.min(Math.max((score / max) * 100, 0), 100);
  const insight = PSS_INSIGHTS[band] ?? PSS_INSIGHTS['Low stress'];

  return (
    <article className={`result-tile result-tile-pss tone-${tone}`}>
      <header className="result-tile-head">
        <div className="result-tile-id">
          <span className="result-tile-tag">PSS-10 · Perceived Stress</span>
          <h3 className="result-tile-title">Your screening result</h3>
        </div>
        <span className="result-tile-band">{band}</span>
      </header>

      <div className="result-tile-body result-tile-body-gad">
        <div className="result-tile-viz">
          <div className="gad-score-block">
            <span className="gad-score-num">{score}</span>
            <span className="gad-score-max">of {max}</span>
          </div>
          <div className="gad-meter" aria-hidden="true">
            <div className="gad-meter-track">
              {PSS_ZONES.map((z) => (
                <span key={z.key} className={`gad-meter-zone zone-${z.key}`} />
              ))}
            </div>
            <span className="gad-meter-marker" style={{ left: `${markerPct}%` }}>
              <span className="gad-meter-dot" />
            </span>
            <div className="gad-meter-labels">
              {PSS_ZONES.map((z) => (
                <span key={z.key}>{z.label}</span>
              ))}
            </div>
          </div>
        </div>

        <div className="result-tile-insights">
          <div className="insight-block">
            <span className="insight-label">What this means</span>
            <p className="insight-summary">{insight.summary}</p>
          </div>
          <div className="insight-block">
            <span className="insight-label">Suggested next steps</span>
            <ul className="insight-steps">
              {insight.steps.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </article>
  );
};

const ASRS_OPTIONS = [
  { value: 0, label: 'Never' },
  { value: 1, label: 'Rarely' },
  { value: 2, label: 'Sometimes' },
  { value: 3, label: 'Often' },
  { value: 4, label: 'Very often' },
];

const ASRS_PART_A = [
  'How often do you have trouble wrapping up the final details of a project, once the challenging parts have been done?',
  'How often do you have difficulty getting things in order when you have to do a task that requires organization?',
  'How often do you have problems remembering appointments or obligations?',
  'When you have a task that requires a lot of thought, how often do you avoid or delay getting started?',
  'How often do you fidget or squirm with your hands or feet when you have to sit down for a long time?',
  'How often do you feel overly active and compelled to do things, like you were driven by a motor?',
];

const ASRS_PART_B = [
  'How often do you make careless mistakes when you have to work on a boring or difficult project?',
  'How often do you have difficulty keeping your attention when you are doing boring or repetitive work?',
  'How often do you have difficulty concentrating on what people say to you, even when they are speaking to you directly?',
  'How often do you misplace or have difficulty finding things at home or at work?',
  'How often are you distracted by activity or noise around you?',
  'How often do you leave your seat in meetings or other situations in which you are expected to remain seated?',
  'How often do you feel restless or fidgety?',
  'How often do you have difficulty unwinding and relaxing when you have time to yourself?',
  'How often do you find yourself talking too much when you are in social situations?',
  "When you're in a conversation, how often do you find yourself finishing the sentences of the people you are talking to, before they can finish?",
  'How often do you have difficulty waiting your turn in situations when turn taking is required?',
  'How often do you interrupt others when they are busy?',
];

const asrsPartASeverity = (score) => {
  if (score <= 9) return { band: 'Low', tone: 'good' };
  if (score <= 13) return { band: 'Mild to moderate', tone: 'mild' };
  if (score <= 17) return { band: 'High', tone: 'moderate' };
  return { band: 'Very high', tone: 'high' };
};

const asrsPartBSeverity = (score) => {
  if (score <= 19) return { band: 'Low', tone: 'good' };
  if (score <= 26) return { band: 'Mild to moderate', tone: 'mild' };
  if (score <= 32) return { band: 'High', tone: 'moderate' };
  return { band: 'Very high', tone: 'high' };
};

const ASRS_INSIGHTS = {
  Low: {
    summary: 'Your Part A score does not suggest probable ADHD at this time.',
    steps: [
      'Re-screen if symptoms begin affecting daily functioning.',
      'Consider other explanations for any difficulties you are experiencing.',
    ],
  },
  'Mild to moderate': {
    summary: 'Your Part A score is in the mild to moderate range.',
    steps: [
      'Discuss these symptoms with a clinician for a fuller picture.',
      'Organisational strategies and structured routines can help manage attention difficulties.',
    ],
  },
  High: {
    summary: 'Your Part A score indicates symptoms consistent with probable ADHD — a clinical evaluation is recommended.',
    steps: [
      'Talk to a clinician — a proper evaluation will clarify the picture.',
      'ADHD is treatable with behavioural strategies and, where indicated, medication.',
      'Early identification leads to better outcomes.',
    ],
  },
  'Very high': {
    summary: 'Your Part A score strongly suggests ADHD symptoms — please seek a clinical evaluation.',
    steps: [
      'Schedule an evaluation with a psychiatrist or psychologist.',
      'Treatment can significantly improve daily functioning.',
      'You don\'t need to struggle alone.',
    ],
  },
};

const ASRS_ZONES = [
  { key: 'good', label: 'Low' },
  { key: 'mild', label: 'Mild–mod.' },
  { key: 'moderate', label: 'High' },
  { key: 'high', label: 'Very high' },
];

const AsrsResultTile = ({ partAScore, partABand, partATone, partBScore, partBBand }) => {
  const partAMax = 24;
  const partBMax = 48;
  const partAMarkerPct = Math.min(Math.max((partAScore / partAMax) * 100, 0), 100);
  const partBMarkerPct = Math.min(Math.max((partBScore / partBMax) * 100, 0), 100);
  const insight = ASRS_INSIGHTS[partABand] ?? ASRS_INSIGHTS.Low;
  const clinicalFlag = partAScore >= 14;

  return (
    <article className={`result-tile result-tile-asrs tone-${partATone}`}>
      <header className="result-tile-head">
        <div className="result-tile-id">
          <span className="result-tile-tag">ASRS · ADHD</span>
          <h3 className="result-tile-title">Your screening result</h3>
        </div>
        <span className="result-tile-band">{partABand}</span>
      </header>

      {clinicalFlag && (
        <div className="safety-banner" role="alert">
          <strong>Part A score ≥ 14 — symptoms consistent with probable ADHD.</strong>
          <p>Talk to your clinician for a full evaluation.</p>
        </div>
      )}

      <div className="result-tile-body result-tile-body-asrs">
        <div className="result-tile-viz asrs-viz">
          <div className="asrs-parts">
            <div className="asrs-part">
              <span className="asrs-part-label">Part A &mdash; 6 questions (max 24)</span>
              <div className="gad-score-block">
                <span className="gad-score-num">{partAScore}</span>
                <span className="gad-score-max">of {partAMax}</span>
              </div>
              <div className="gad-meter" aria-hidden="true">
                <div className="gad-meter-track">
                  {ASRS_ZONES.map((z) => (
                    <span key={z.key} className={`gad-meter-zone zone-${z.key}`} />
                  ))}
                </div>
                <span className="gad-meter-marker" style={{ left: `${partAMarkerPct}%` }}>
                  <span className="gad-meter-dot" />
                </span>
                <div className="gad-meter-labels">
                  {ASRS_ZONES.map((z) => <span key={z.key}>{z.label}</span>)}
                </div>
              </div>
              <span className="asrs-part-band">{partABand}</span>
            </div>

            <div className="asrs-part">
              <span className="asrs-part-label">Part B &mdash; 12 questions (max 48)</span>
              <div className="gad-score-block">
                <span className="gad-score-num">{partBScore}</span>
                <span className="gad-score-max">of {partBMax}</span>
              </div>
              <div className="gad-meter" aria-hidden="true">
                <div className="gad-meter-track">
                  {ASRS_ZONES.map((z) => (
                    <span key={z.key} className={`gad-meter-zone zone-${z.key}`} />
                  ))}
                </div>
                <span className="gad-meter-marker" style={{ left: `${partBMarkerPct}%` }}>
                  <span className="gad-meter-dot" />
                </span>
                <div className="gad-meter-labels">
                  {ASRS_ZONES.map((z) => <span key={z.key}>{z.label}</span>)}
                </div>
              </div>
              <span className="asrs-part-band">{partBBand}</span>
            </div>
          </div>
        </div>

        <div className="result-tile-insights">
          <div className="insight-block">
            <span className="insight-label">What this means</span>
            <p className="insight-summary">{insight.summary}</p>
          </div>
          <div className="insight-block">
            <span className="insight-label">Suggested next steps</span>
            <ul className="insight-steps">
              {insight.steps.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </article>
  );
};

const ISI_SEVERITY_OPTIONS = [
  { value: 0, label: 'None' },
  { value: 1, label: 'Mild' },
  { value: 2, label: 'Moderate' },
  { value: 3, label: 'Severe' },
];

const ISI_QUESTIONS = [
  {
    text: 'Do you have difficulty falling asleep?',
    options: ISI_SEVERITY_OPTIONS,
  },
  {
    text: 'Do you have difficulty staying asleep?',
    options: ISI_SEVERITY_OPTIONS,
  },
  {
    text: 'Do you have problems waking up too early?',
    options: ISI_SEVERITY_OPTIONS,
  },
  {
    text: 'How satisfied or dissatisfied are you with your current sleep pattern?',
    options: [
      { value: 0, label: 'Very satisfied' },
      { value: 1, label: 'Satisfied' },
      { value: 2, label: 'Moderately satisfied' },
      { value: 3, label: 'Dissatisfied' },
      { value: 4, label: 'Very dissatisfied' },
    ],
  },
  {
    text: 'How noticeable to others do you think your sleep problem is in terms of impairing the quality of your life?',
    options: [
      { value: 0, label: 'Not at all noticeable' },
      { value: 1, label: 'A little' },
      { value: 2, label: 'Somewhat' },
      { value: 3, label: 'Much' },
      { value: 4, label: 'Very much noticeable' },
    ],
  },
  {
    text: 'How worried or distressed are you about your current sleep problem?',
    options: [
      { value: 0, label: 'Not at all worried' },
      { value: 1, label: 'A little' },
      { value: 2, label: 'Somewhat' },
      { value: 3, label: 'Much' },
      { value: 4, label: 'Very much worried' },
    ],
  },
  {
    text: 'To what extent do you consider your sleep problems to interfere with your daily functioning (e.g. daytime fatigue, mood, concentration)?',
    options: [
      { value: 0, label: 'Not at all interfering' },
      { value: 1, label: 'A little' },
      { value: 2, label: 'Somewhat' },
      { value: 3, label: 'Much' },
      { value: 4, label: 'Very much interfering' },
    ],
  },
];

const isiSeverity = (score) => {
  if (score <= 7) return { band: 'No insomnia', tone: 'good' };
  if (score <= 14) return { band: 'Subthreshold insomnia', tone: 'mild' };
  if (score <= 21) return { band: 'Moderate insomnia', tone: 'moderate' };
  return { band: 'Severe insomnia', tone: 'high' };
};

const ISI_INSIGHTS = {
  'No insomnia': {
    summary: 'Your responses suggest no clinically significant insomnia at this time.',
    steps: [
      'Maintain consistent sleep and wake times.',
      'Re-screen if sleep difficulties emerge or worsen.',
    ],
  },
  'Subthreshold insomnia': {
    summary: 'You show mild sleep difficulties that fall below the clinical threshold for insomnia.',
    steps: [
      'Good sleep hygiene — consistent schedule, limiting screens before bed — can help.',
      'Reduce caffeine intake, especially after midday.',
      'Re-screen in a few weeks if symptoms persist.',
    ],
  },
  'Moderate insomnia': {
    summary: 'Your score suggests moderate insomnia that is likely affecting your daily life.',
    steps: [
      'Consider speaking to a clinician about your sleep.',
      'Cognitive Behavioural Therapy for Insomnia (CBT-I) is the most effective non-medication treatment.',
      'Medication may be appropriate in some cases — discuss with your doctor.',
    ],
  },
  'Severe insomnia': {
    summary: 'Your score indicates severe insomnia — professional support is recommended.',
    steps: [
      'Seek evaluation from a clinician as soon as possible.',
      'CBT-I and/or pharmacotherapy can significantly improve sleep quality.',
      'Untreated severe insomnia affects physical health, mood, and cognitive function.',
    ],
  },
};

const ISI_ZONES = [
  { key: 'good', label: 'None' },
  { key: 'mild', label: 'Subthreshold' },
  { key: 'moderate', label: 'Moderate' },
  { key: 'high', label: 'Severe' },
];

const IsiResultTile = ({ score, band, tone }) => {
  const max = 28;
  const markerPct = Math.min(Math.max((score / max) * 100, 0), 100);
  const insight = ISI_INSIGHTS[band] ?? ISI_INSIGHTS['No insomnia'];

  return (
    <article className={`result-tile result-tile-isi tone-${tone}`}>
      <header className="result-tile-head">
        <div className="result-tile-id">
          <span className="result-tile-tag">ISI · Insomnia</span>
          <h3 className="result-tile-title">Your screening result</h3>
        </div>
        <span className="result-tile-band">{band}</span>
      </header>

      <div className="result-tile-body result-tile-body-gad">
        <div className="result-tile-viz">
          <div className="gad-score-block">
            <span className="gad-score-num">{score}</span>
            <span className="gad-score-max">of {max}</span>
          </div>
          <div className="gad-meter" aria-hidden="true">
            <div className="gad-meter-track">
              {ISI_ZONES.map((z) => (
                <span key={z.key} className={`gad-meter-zone zone-${z.key}`} />
              ))}
            </div>
            <span className="gad-meter-marker" style={{ left: `${markerPct}%` }}>
              <span className="gad-meter-dot" />
            </span>
            <div className="gad-meter-labels">
              {ISI_ZONES.map((z) => (
                <span key={z.key}>{z.label}</span>
              ))}
            </div>
          </div>
        </div>

        <div className="result-tile-insights">
          <div className="insight-block">
            <span className="insight-label">What this means</span>
            <p className="insight-summary">{insight.summary}</p>
          </div>
          <div className="insight-block">
            <span className="insight-label">Suggested next steps</span>
            <ul className="insight-steps">
              {insight.steps.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </article>
  );
};

const MDQ_ITEMS = [
  'Felt so good or hyper that other people thought you were not your normal self, or you were so hyper that you got into trouble?',
  'Were so irritable that you shouted at people or started fights or arguments?',
  'Felt much more self-confident?',
  'Felt much more confident than usual?',
  'Got much less sleep than usual and found you did not miss it?',
  'Were much more talkative or spoke faster than usual?',
  'Thoughts raced through your head or you couldn\'t slow your mind down?',
  'Were so easily distracted by things around you that you had trouble concentrating or staying on track?',
  'Had much more energy than usual?',
  'Were much more active or did many more things than usual?',
  'Were more social or outgoing than usual?',
  'Were more interested in sex than usual?',
  'Did things that were unusual or that other people might have thought were excessive, foolish, or risky?',
  'Spending money got you or your family into trouble?',
];

const MDQ_YN_OPTIONS = [
  { value: 1, label: 'Yes' },
  { value: 0, label: 'No' },
];

const MDQ_Q3_OPTIONS = ['No problem', 'Minor problem', 'Moderate problem', 'Serious problem'];

const MdqBinaryRow = ({ label, text, name, value, onChange }) => (
  <fieldset className="scale-row">
    <legend className="scale-row-q">
      <span className="scale-row-num">{label}.</span> {text}
    </legend>
    <div className="scale-row-options mdq-yn-options">
      {MDQ_YN_OPTIONS.map((opt) => {
        const id = `${name}-${opt.value}`;
        return (
          <label key={opt.value} htmlFor={id} className={`scale-opt ${value === opt.value ? 'is-selected' : ''}`}>
            <input
              id={id}
              type="radio"
              name={name}
              value={opt.value}
              checked={value === opt.value}
              onChange={() => onChange(opt.value)}
            />
            <span className="scale-opt-label">{opt.label}</span>
          </label>
        );
      })}
    </div>
  </fieldset>
);

const MdqResultTile = ({ itemScore, q2, q3 }) => {
  const criterion1 = itemScore >= 7;
  const criterion2 = q2 === 1;
  const criterion3 = q3 === 'Moderate problem' || q3 === 'Serious problem';
  const positive = criterion1 && criterion2 && criterion3;
  const tone = positive ? 'high' : 'good';

  const criteria = [
    {
      met: criterion1,
      label: '7 or more symptoms endorsed',
      detail: `${itemScore} of 14 items marked Yes (threshold: 7)`,
    },
    {
      met: criterion2,
      label: 'Symptoms occurred during the same period',
      detail: q2 === 1 ? 'Yes' : 'No',
    },
    {
      met: criterion3,
      label: 'Symptoms caused moderate or serious problems',
      detail: q3 || '—',
    },
  ];

  return (
    <article className={`result-tile result-tile-mdq tone-${tone}`}>
      <header className="result-tile-head">
        <div className="result-tile-id">
          <span className="result-tile-tag">MDQ · Mood Disorder</span>
          <h3 className="result-tile-title">Your screening result</h3>
        </div>
        <span className="result-tile-band">{positive ? 'Warrants evaluation' : 'Screen not positive'}</span>
      </header>

      <div className="result-tile-body result-tile-body-mdq">
        <div className="result-tile-viz mdq-criteria-wrap">
          {criteria.map((c) => (
            <div key={c.label} className={`mdq-criterion ${c.met ? 'mdq-criterion-met' : 'mdq-criterion-unmet'}`}>
              <span className="mdq-criterion-icon" aria-hidden="true">{c.met ? '✓' : '✗'}</span>
              <div className="mdq-criterion-text">
                <span className="mdq-criterion-label">{c.label}</span>
                <span className="mdq-criterion-detail">{c.detail}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="result-tile-insights">
          <div className="insight-block">
            <span className="insight-label">What this means</span>
            <p className="insight-summary">
              {positive
                ? 'All three screening criteria are met. This suggests a possibility of a mood disorder such as bipolar disorder. A formal evaluation by a clinician is recommended.'
                : 'The screening criteria for a probable mood disorder are not fully met. This does not rule out a mood disorder — a clinician can provide a fuller picture if you have concerns.'}
            </p>
          </div>
          <div className="insight-block">
            <span className="insight-label">Suggested next steps</span>
            <ul className="insight-steps">
              {positive ? (
                <>
                  <li>Schedule an appointment with a psychiatrist or trained clinician.</li>
                  <li>Bring these results to help start the conversation.</li>
                  <li>Mood disorders are treatable — early identification matters.</li>
                </>
              ) : (
                <>
                  <li>If you have ongoing mood concerns, discuss them with a clinician.</li>
                  <li>Re-screen if your mood patterns change significantly.</li>
                </>
              )}
            </ul>
          </div>
        </div>
      </div>
    </article>
  );
};

const WHO5_OPTIONS = [
  { value: 0, label: 'At no time' },
  { value: 1, label: 'Some of the time' },
  { value: 2, label: 'Less than half the time' },
  { value: 3, label: 'More than half the time' },
  { value: 4, label: 'Most of the time' },
  { value: 5, label: 'All the time' },
];

const WHO5_QUESTIONS = [
  'I have felt cheerful and in good spirits.',
  'I have felt calm and relaxed.',
  'I have felt active and vigorous.',
  'I woke up feeling fresh and rested.',
  'My daily life has been filled with things that interest me.',
];

const who5Severity = (score) => {
  if (score <= 8) return { band: 'Very low', tone: 'high' };
  if (score <= 13) return { band: 'Low', tone: 'moderate' };
  if (score <= 17) return { band: 'Moderate', tone: 'mild' };
  return { band: 'Good', tone: 'good' };
};

const WHO5_INSIGHTS = {
  'Very low': {
    summary: 'Your well-being score is very low. Underlying mental health conditions may be contributing and warrant assessment.',
    steps: [
      'A low WHO-5 score is associated with possible depression — consider completing a PHQ-9 screen.',
      'Reach out to a clinician or counsellor.',
      'Small, consistent changes to sleep, activity, and social connection can help over time.',
    ],
  },
  Low: {
    summary: 'Your well-being is below average. It may be worth exploring what is affecting your quality of life.',
    steps: [
      'Speak to a clinician if this has persisted for several weeks.',
      'Identify the area of life causing the most difficulty — work, sleep, or relationships.',
      'Physical activity and social support have strong evidence for improving well-being.',
    ],
  },
  Moderate: {
    summary: 'Your well-being is in the moderate range — there is room to improve your quality of life.',
    steps: [
      'Focus on what feels most draining and consider small, manageable changes.',
      'Mindfulness and regular exercise have good evidence for lifting well-being.',
      'Consider speaking to someone if things do not improve over the coming weeks.',
    ],
  },
  Good: {
    summary: 'Your well-being score is good — you are experiencing a reasonable quality of life.',
    steps: [
      'Keep up the habits and connections that are working for you.',
      'Re-screen if your circumstances change significantly.',
    ],
  },
};

const Who5ResultTile = ({ score, band, tone }) => {
  const max = 25;
  const radius = 56;
  const circumference = 2 * Math.PI * radius;
  const progress = Math.min(Math.max(score / max, 0), 1);
  const dashOffset = circumference * (1 - progress);
  const pct = score * 4;
  const insight = WHO5_INSIGHTS[band] ?? WHO5_INSIGHTS.Good;

  return (
    <article className={`result-tile result-tile-who5 tone-${tone}`}>
      <header className="result-tile-head">
        <div className="result-tile-id">
          <span className="result-tile-tag">WHO-5 · Well-being</span>
          <h3 className="result-tile-title">Your screening result</h3>
        </div>
        <span className="result-tile-band">{band}</span>
      </header>

      <div className="result-tile-body result-tile-body-phq">
        <div className="result-tile-viz">
          <div className="phq-ring-wrap" aria-hidden="true">
            <svg className="phq-ring" viewBox="0 0 140 140">
              <circle className="phq-ring-track" cx="70" cy="70" r={radius} />
              <circle
                className="phq-ring-progress"
                cx="70"
                cy="70"
                r={radius}
                style={{
                  strokeDasharray: circumference,
                  strokeDashoffset: dashOffset,
                }}
              />
            </svg>
            <div className="phq-ring-center">
              <div className="phq-ring-score">{score}</div>
              <div className="phq-ring-max">of {max}</div>
            </div>
          </div>
          <span className="result-tile-vizlabel">Well-being score · {pct}% · past 2 weeks</span>
        </div>

        <div className="result-tile-insights">
          <div className="insight-block">
            <span className="insight-label">What this means</span>
            <p className="insight-summary">{insight.summary}</p>
          </div>
          <div className="insight-block">
            <span className="insight-label">Suggested next steps</span>
            <ul className="insight-steps">
              {insight.steps.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </article>
  );
};

const OCI4_OPTIONS = [
  { value: 0, label: 'Not at all' },
  { value: 1, label: 'A little' },
  { value: 2, label: 'Moderately' },
  { value: 3, label: 'A lot' },
  { value: 4, label: 'Extremely' },
];

const OCI4_QUESTIONS = [
  'I get upset if objects are not arranged properly.',
  'I repeatedly check doors, windows, drawers, etc.',
  'I sometimes have to wash or clean myself simply because I feel contaminated.',
  'I frequently get nasty thoughts and have difficulty getting rid of them.',
];

const oci4Severity = (score) => {
  if (score <= 2) return { band: 'Below threshold', tone: 'good' };
  return { band: 'May indicate OCD', tone: 'high' };
};

const OCI4_INSIGHTS = {
  'Below threshold': {
    summary: 'Your score is below the threshold for clinically significant OCD symptoms.',
    steps: [
      'Re-screen if intrusive thoughts or repetitive behaviours become more distressing.',
      'Acceptance and mindfulness techniques can help manage occasional unwanted thoughts.',
    ],
  },
  'May indicate OCD': {
    summary: 'Your score is at or above the threshold that may indicate OCD. A clinician can help clarify the picture.',
    steps: [
      'Consult a clinician for a definitive evaluation — OCD is treatable.',
      'Cognitive Behavioural Therapy with Exposure and Response Prevention (ERP) is the most effective treatment.',
      'Medication may also help — discuss options with your doctor.',
    ],
  },
};

const Oci4ResultTile = ({ score, band, tone }) => {
  const max = 16;
  const radius = 56;
  const circumference = 2 * Math.PI * radius;
  const progress = Math.min(Math.max(score / max, 0), 1);
  const dashOffset = circumference * (1 - progress);
  const insight = OCI4_INSIGHTS[band] ?? OCI4_INSIGHTS['Below threshold'];
  const clinicalFlag = score >= 3;

  return (
    <article className={`result-tile result-tile-oci4 tone-${tone}`}>
      <header className="result-tile-head">
        <div className="result-tile-id">
          <span className="result-tile-tag">OCI-4 · OCD</span>
          <h3 className="result-tile-title">Your screening result</h3>
        </div>
        <span className="result-tile-band">{band}</span>
      </header>

      {clinicalFlag && (
        <div className="safety-banner" role="alert">
          <strong>Score ≥ 3 — symptoms may indicate OCD.</strong>
          <p>Consult a clinician for a definitive evaluation.</p>
        </div>
      )}

      <div className="result-tile-body result-tile-body-phq">
        <div className="result-tile-viz">
          <div className="phq-ring-wrap" aria-hidden="true">
            <svg className="phq-ring" viewBox="0 0 140 140">
              <circle className="phq-ring-track" cx="70" cy="70" r={radius} />
              <circle
                className="phq-ring-progress"
                cx="70"
                cy="70"
                r={radius}
                style={{
                  strokeDasharray: circumference,
                  strokeDashoffset: dashOffset,
                }}
              />
            </svg>
            <div className="phq-ring-center">
              <div className="phq-ring-score">{score}</div>
              <div className="phq-ring-max">of {max}</div>
            </div>
          </div>
          <span className="result-tile-vizlabel">Symptom score · past month</span>
        </div>

        <div className="result-tile-insights">
          <div className="insight-block">
            <span className="insight-label">What this means</span>
            <p className="insight-summary">{insight.summary}</p>
          </div>
          <div className="insight-block">
            <span className="insight-label">Suggested next steps</span>
            <ul className="insight-steps">
              {insight.steps.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </article>
  );
};

const EPDS_QUESTIONS = [
  {
    text: 'I have been able to laugh and see the funny side of things.',
    options: [
      { value: 0, label: 'As much as I always could' },
      { value: 1, label: 'Not quite so much now' },
      { value: 2, label: 'Definitely not so much now' },
      { value: 3, label: 'Not at all' },
    ],
  },
  {
    text: 'I have looked forward with enjoyment to things.',
    options: [
      { value: 0, label: 'As much as I ever did' },
      { value: 1, label: 'Rather less than I used to' },
      { value: 2, label: 'Definitely less than I used to' },
      { value: 3, label: 'Hardly at all' },
    ],
  },
  {
    text: 'I have blamed myself unnecessarily when things went wrong.',
    options: [
      { value: 3, label: 'Yes, most of the time' },
      { value: 2, label: 'Yes, some of the time' },
      { value: 1, label: 'Not very often' },
      { value: 0, label: 'No, never' },
    ],
  },
  {
    text: 'I have been anxious or worried for no good reason.',
    options: [
      { value: 0, label: 'No, not at all' },
      { value: 1, label: 'Hardly ever' },
      { value: 2, label: 'Yes, sometimes' },
      { value: 3, label: 'Yes, very often' },
    ],
  },
  {
    text: 'I have felt scared or panicky for no very good reason.',
    options: [
      { value: 3, label: 'Yes, quite a lot' },
      { value: 2, label: 'Yes, sometimes' },
      { value: 1, label: 'No, not much' },
      { value: 0, label: 'No, not at all' },
    ],
  },
  {
    text: 'Things have been getting on top of me.',
    options: [
      { value: 3, label: "Yes, most of the time I haven't been able to cope at all" },
      { value: 2, label: "Yes, sometimes I haven't been coping as well as usual" },
      { value: 1, label: 'No, most of the time I have coped quite well' },
      { value: 0, label: 'No, I have been coping as well as ever' },
    ],
  },
  {
    text: 'I have been so unhappy that I have had difficulty sleeping.',
    options: [
      { value: 3, label: 'Yes, most of the time' },
      { value: 2, label: 'Yes, sometimes' },
      { value: 1, label: 'Not very often' },
      { value: 0, label: 'No, not at all' },
    ],
  },
  {
    text: 'I have felt sad or miserable.',
    options: [
      { value: 3, label: 'Yes, most of the time' },
      { value: 2, label: 'Yes, sometimes' },
      { value: 1, label: 'Not very often' },
      { value: 0, label: 'No, not at all' },
    ],
  },
  {
    text: 'I have been so unhappy that I have been crying.',
    options: [
      { value: 3, label: 'Yes, most of the time' },
      { value: 2, label: 'Yes, quite often' },
      { value: 1, label: 'Only occasionally' },
      { value: 0, label: 'No, never' },
    ],
  },
  {
    text: 'The thought of harming myself has occurred to me.',
    options: [
      { value: 3, label: 'Yes, quite often' },
      { value: 2, label: 'Sometimes' },
      { value: 1, label: 'Hardly ever' },
      { value: 0, label: 'Never' },
    ],
  },
];

const epdsSeverity = (score) => {
  if (score <= 9) return { band: 'Below threshold', tone: 'good' };
  if (score <= 12) return { band: 'Possible depression', tone: 'moderate' };
  return { band: 'Likely depression', tone: 'high' };
};

const EPDS_INSIGHTS = {
  'Below threshold': {
    summary: 'Your score does not indicate postnatal depression at this time.',
    steps: [
      'Continue to check in with yourself regularly — postnatal mood can shift.',
      'Reach out to your midwife or doctor if things change.',
      'Re-screening monthly during the postnatal period is reasonable.',
    ],
  },
  'Possible depression': {
    summary: 'Your score suggests a possibility of postnatal depression. A clinician can help clarify and support you.',
    steps: [
      'Talk to your midwife, GP, or obstetrician about how you are feeling.',
      'Postnatal depression is common, treatable, and not a reflection of your ability as a parent.',
      'Support from family and peer groups can also make a real difference.',
    ],
  },
  'Likely depression': {
    summary: 'Your score indicates a significant likelihood of postnatal depression. Please reach out to a clinician soon.',
    steps: [
      'Contact your midwife, GP, or a mental health professional as soon as possible.',
      'Effective treatments — including therapy and medication — are available and safe during the postnatal period.',
      'You are not alone. Postnatal depression is very common and very treatable.',
    ],
  },
};

const EpdsResultTile = ({ score, band, tone, safetyFlag }) => {
  const max = 30;
  const radius = 56;
  const circumference = 2 * Math.PI * radius;
  const progress = Math.min(Math.max(score / max, 0), 1);
  const dashOffset = circumference * (1 - progress);
  const insight = EPDS_INSIGHTS[band] ?? EPDS_INSIGHTS['Below threshold'];

  return (
    <article className={`result-tile result-tile-epds tone-${tone}`}>
      <header className="result-tile-head">
        <div className="result-tile-id">
          <span className="result-tile-tag">EPDS · Postnatal Depression</span>
          <h3 className="result-tile-title">Your screening result</h3>
        </div>
        <span className="result-tile-band">{band}</span>
      </header>

      {safetyFlag && (
        <div className="safety-banner" role="alert">
          <strong>You indicated thoughts of self-harm.</strong>
          <p>
            Please reach out for support right now. India's 24×7 toll-free helpline{' '}
            <b>Tele-MANAS 14416</b> is available, or you can speak to your midwife or GP immediately.
          </p>
        </div>
      )}

      <div className="result-tile-body result-tile-body-phq">
        <div className="result-tile-viz">
          <div className="phq-ring-wrap" aria-hidden="true">
            <svg className="phq-ring" viewBox="0 0 140 140">
              <circle className="phq-ring-track" cx="70" cy="70" r={radius} />
              <circle
                className="phq-ring-progress"
                cx="70"
                cy="70"
                r={radius}
                style={{
                  strokeDasharray: circumference,
                  strokeDashoffset: dashOffset,
                }}
              />
            </svg>
            <div className="phq-ring-center">
              <div className="phq-ring-score">{score}</div>
              <div className="phq-ring-max">of {max}</div>
            </div>
          </div>
          <span className="result-tile-vizlabel">Symptom score · past 7 days</span>
        </div>

        <div className="result-tile-insights">
          <div className="insight-block">
            <span className="insight-label">What this means</span>
            <p className="insight-summary">{insight.summary}</p>
          </div>
          <div className="insight-block">
            <span className="insight-label">Suggested next steps</span>
            <ul className="insight-steps">
              {insight.steps.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </article>
  );
};

const SCALES = [
  { key: 'phq',   label: 'PHQ-9',  subtitle: 'Depression',          questions: 9,  period: '2 weeks'  },
  { key: 'gad',   label: 'GAD-7',  subtitle: 'Anxiety',             questions: 7,  period: '2 weeks'  },
  { key: 'audit', label: 'AUDIT',  subtitle: 'Alcohol Use',          questions: 10, period: '1 year'   },
  { key: 'ftnd',  label: 'FTND',   subtitle: 'Nicotine Dependence',  questions: 6,  period: 'Current'  },
  { key: 'pss',   label: 'PSS-10', subtitle: 'Perceived Stress',     questions: 10, period: '1 month'  },
  { key: 'asrs',  label: 'ASRS',   subtitle: 'ADHD',                 questions: 18, period: '6 months' },
  { key: 'isi',   label: 'ISI',    subtitle: 'Insomnia',             questions: 7,  period: '2 weeks'  },
  { key: 'mdq',   label: 'MDQ',    subtitle: 'Mood Disorder',        questions: 16, period: 'Lifetime' },
  { key: 'who5',  label: 'WHO-5',  subtitle: 'Well-being',           questions: 5,  period: '2 weeks'  },
  { key: 'oci4',  label: 'OCI-4',  subtitle: 'OCD',                  questions: 4,  period: '1 month'  },
  { key: 'epds',  label: 'EPDS',   subtitle: 'Postnatal Depression', questions: 10, period: '7 days'   },
];

const SelfAssessment = () => {
  const [activeScale, setActiveScale] = useState(null);
  const [meta, setMeta] = useState({ name: '', dob: '', date: new Date().toISOString().slice(0, 10) });
  const [phq, setPhq] = useState(Array(PHQ9_QUESTIONS.length).fill(null));
  const [gad, setGad] = useState(Array(GAD7_QUESTIONS.length).fill(null));
  const [phqDifficulty, setPhqDifficulty] = useState('');
  const [gadDifficulty, setGadDifficulty] = useState('');
  const [phqSubmitted, setPhqSubmitted] = useState(false);
  const [gadSubmitted, setGadSubmitted] = useState(false);
  const [auditSubmitted, setAuditSubmitted] = useState(false);
  const [phqError, setPhqError] = useState('');
  const [gadError, setGadError] = useState('');
  const [auditError, setAuditError] = useState('');
  const [audit, setAudit] = useState(Array(AUDIT_QUESTIONS.length).fill(null));
  const [auditTreatment, setAuditTreatment] = useState('');
  const [ftnd, setFtnd] = useState(Array(FTND_QUESTIONS.length).fill(null));
  const [ftndSubmitted, setFtndSubmitted] = useState(false);
  const [ftndError, setFtndError] = useState('');
  const [pss, setPss] = useState(Array(PSS_QUESTIONS.length).fill(null));
  const [pssSubmitted, setPssSubmitted] = useState(false);
  const [pssError, setPssError] = useState('');
  const [asrs, setAsrs] = useState(Array(ASRS_PART_A.length + ASRS_PART_B.length).fill(null));
  const [asrsSubmitted, setAsrsSubmitted] = useState(false);
  const [asrsError, setAsrsError] = useState('');
  const [isi, setIsi] = useState(Array(ISI_QUESTIONS.length).fill(null));
  const [isiSubmitted, setIsiSubmitted] = useState(false);
  const [isiError, setIsiError] = useState('');
  const [mdqItems, setMdqItems] = useState(Array(MDQ_ITEMS.length).fill(null));
  const [mdqQ2, setMdqQ2] = useState(null);
  const [mdqQ3, setMdqQ3] = useState('');
  const [mdqSubmitted, setMdqSubmitted] = useState(false);
  const [mdqError, setMdqError] = useState('');
  const [who5, setWho5] = useState(Array(WHO5_QUESTIONS.length).fill(null));
  const [who5Submitted, setWho5Submitted] = useState(false);
  const [who5Error, setWho5Error] = useState('');
  const [oci4, setOci4] = useState(Array(OCI4_QUESTIONS.length).fill(null));
  const [oci4Submitted, setOci4Submitted] = useState(false);
  const [oci4Error, setOci4Error] = useState('');
  const [epds, setEpds] = useState(Array(EPDS_QUESTIONS.length).fill(null));
  const [epdsSubmitted, setEpdsSubmitted] = useState(false);
  const [epdsError, setEpdsError] = useState('');
  const phqResultRef = useRef(null);
  const gadResultRef = useRef(null);
  const auditResultRef = useRef(null);
  const ftndResultRef = useRef(null);
  const pssResultRef = useRef(null);
  const asrsResultRef = useRef(null);
  const isiResultRef = useRef(null);
  const mdqResultRef = useRef(null);
  const who5ResultRef = useRef(null);
  const oci4ResultRef = useRef(null);
  const epdsResultRef = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  const setPhqAt = (i, v) => setPhq((prev) => prev.map((p, idx) => (idx === i ? v : p)));
  const setGadAt = (i, v) => setGad((prev) => prev.map((p, idx) => (idx === i ? v : p)));
  const setAuditAt = (i, v) => setAudit((prev) => prev.map((p, idx) => (idx === i ? v : p)));
  const setFtndAt = (i, v) => setFtnd((prev) => prev.map((p, idx) => (idx === i ? v : p)));
  const setPssAt = (i, v) => setPss((prev) => prev.map((p, idx) => (idx === i ? v : p)));
  const setAsrsAt = (i, v) => setAsrs((prev) => prev.map((p, idx) => (idx === i ? v : p)));
  const setIsiAt = (i, v) => setIsi((prev) => prev.map((p, idx) => (idx === i ? v : p)));
  const setMdqItemAt = (i, v) => setMdqItems((prev) => prev.map((p, idx) => (idx === i ? v : p)));
  const setWho5At = (i, v) => setWho5((prev) => prev.map((p, idx) => (idx === i ? v : p)));
  const setOci4At = (i, v) => setOci4((prev) => prev.map((p, idx) => (idx === i ? v : p)));
  const setEpdsAt = (i, v) => setEpds((prev) => prev.map((p, idx) => (idx === i ? v : p)));

  const phqTotal = useMemo(() => phq.reduce((s, v) => s + (v ?? 0), 0), [phq]);
  const gadTotal = useMemo(() => gad.reduce((s, v) => s + (v ?? 0), 0), [gad]);
  const auditTotal = useMemo(() => audit.reduce((s, v) => s + (v ?? 0), 0), [audit]);
  const ftndTotal = useMemo(() => ftnd.reduce((s, v) => s + (v ?? 0), 0), [ftnd]);
  const pssTotal = useMemo(() => pss.reduce((s, v) => s + (v ?? 0), 0), [pss]);
  const asrsPartATotal = useMemo(() => asrs.slice(0, ASRS_PART_A.length).reduce((s, v) => s + (v ?? 0), 0), [asrs]);
  const asrsPartBTotal = useMemo(() => asrs.slice(ASRS_PART_A.length).reduce((s, v) => s + (v ?? 0), 0), [asrs]);
  const isiTotal = useMemo(() => isi.reduce((s, v) => s + (v ?? 0), 0), [isi]);
  const mdqScore = useMemo(() => mdqItems.reduce((s, v) => s + (v ?? 0), 0), [mdqItems]);
  const who5Total = useMemo(() => who5.reduce((s, v) => s + (v ?? 0), 0), [who5]);
  const oci4Total = useMemo(() => oci4.reduce((s, v) => s + (v ?? 0), 0), [oci4]);
  const epdsTotal = useMemo(() => epds.reduce((s, v) => s + (v ?? 0), 0), [epds]);

  const allPhqAnswered = phq.every((v) => v !== null);
  const allGadAnswered = gad.every((v) => v !== null);
  const allAuditAnswered = audit.every((v) => v !== null);
  const allFtndAnswered = ftnd.every((v) => v !== null);
  const allPssAnswered = pss.every((v) => v !== null);
  const allAsrsAnswered = asrs.every((v) => v !== null);
  const allIsiAnswered = isi.every((v) => v !== null);
  const allMdqAnswered = mdqItems.every((v) => v !== null) && mdqQ2 !== null && mdqQ3 !== '';
  const allWho5Answered = who5.every((v) => v !== null);
  const allOci4Answered = oci4.every((v) => v !== null);
  const allEpdsAnswered = epds.every((v) => v !== null);

  const handlePhqSubmit = (e) => {
    e.preventDefault();
    if (!allPhqAnswered) {
      setPhqError('Please answer every PHQ-9 question before submitting.');
      return;
    }
    setPhqError('');
    setPhqSubmitted(true);
    setTimeout(() => {
      phqResultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const handleGadSubmit = (e) => {
    e.preventDefault();
    if (!allGadAnswered) {
      setGadError('Please answer every GAD-7 question before submitting.');
      return;
    }
    setGadError('');
    setGadSubmitted(true);
    setTimeout(() => {
      gadResultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const handlePhqReset = () => {
    setPhq(Array(PHQ9_QUESTIONS.length).fill(null));
    setPhqDifficulty('');
    setPhqSubmitted(false);
    setPhqError('');
  };

  const handleGadReset = () => {
    setGad(Array(GAD7_QUESTIONS.length).fill(null));
    setGadDifficulty('');
    setGadSubmitted(false);
    setGadError('');
  };

  const handleAuditSubmit = (e) => {
    e.preventDefault();
    if (!allAuditAnswered) {
      setAuditError('Please answer every AUDIT question before submitting.');
      return;
    }
    setAuditError('');
    setAuditSubmitted(true);
    setTimeout(() => {
      auditResultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const handleAuditReset = () => {
    setAudit(Array(AUDIT_QUESTIONS.length).fill(null));
    setAuditTreatment('');
    setAuditSubmitted(false);
    setAuditError('');
  };

  const phqResult = phq9Severity(phqTotal);
  const gadResult = gad7Severity(gadTotal);
  const handleFtndSubmit = (e) => {
    e.preventDefault();
    if (!allFtndAnswered) {
      setFtndError('Please answer every question before submitting.');
      return;
    }
    setFtndError('');
    setFtndSubmitted(true);
    setTimeout(() => {
      ftndResultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const handleFtndReset = () => {
    setFtnd(Array(FTND_QUESTIONS.length).fill(null));
    setFtndSubmitted(false);
    setFtndError('');
  };

  const handlePssSubmit = (e) => {
    e.preventDefault();
    if (!allPssAnswered) {
      setPssError('Please answer every question before submitting.');
      return;
    }
    setPssError('');
    setPssSubmitted(true);
    setTimeout(() => {
      pssResultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const handlePssReset = () => {
    setPss(Array(PSS_QUESTIONS.length).fill(null));
    setPssSubmitted(false);
    setPssError('');
  };

  const handleAsrsSubmit = (e) => {
    e.preventDefault();
    if (!allAsrsAnswered) {
      setAsrsError('Please answer every question before submitting.');
      return;
    }
    setAsrsError('');
    setAsrsSubmitted(true);
    setTimeout(() => {
      asrsResultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const handleAsrsReset = () => {
    setAsrs(Array(ASRS_PART_A.length + ASRS_PART_B.length).fill(null));
    setAsrsSubmitted(false);
    setAsrsError('');
  };

  const auditResult = auditSeverity(auditTotal);
  const ftndResult = ftndSeverity(ftndTotal);
  const pssResult = pssSeverity(pssTotal);
  const handleIsiSubmit = (e) => {
    e.preventDefault();
    if (!allIsiAnswered) {
      setIsiError('Please answer every question before submitting.');
      return;
    }
    setIsiError('');
    setIsiSubmitted(true);
    setTimeout(() => {
      isiResultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const handleIsiReset = () => {
    setIsi(Array(ISI_QUESTIONS.length).fill(null));
    setIsiSubmitted(false);
    setIsiError('');
  };

  const asrsPartAResult = asrsPartASeverity(asrsPartATotal);
  const asrsPartBResult = asrsPartBSeverity(asrsPartBTotal);
  const handleMdqSubmit = (e) => {
    e.preventDefault();
    if (!allMdqAnswered) {
      setMdqError('Please answer all questions including Q2 and Q3 before submitting.');
      return;
    }
    setMdqError('');
    setMdqSubmitted(true);
    setTimeout(() => {
      mdqResultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const handleMdqReset = () => {
    setMdqItems(Array(MDQ_ITEMS.length).fill(null));
    setMdqQ2(null);
    setMdqQ3('');
    setMdqSubmitted(false);
    setMdqError('');
  };

  const handleWho5Submit = (e) => {
    e.preventDefault();
    if (!allWho5Answered) {
      setWho5Error('Please answer every question before submitting.');
      return;
    }
    setWho5Error('');
    setWho5Submitted(true);
    setTimeout(() => {
      who5ResultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const handleWho5Reset = () => {
    setWho5(Array(WHO5_QUESTIONS.length).fill(null));
    setWho5Submitted(false);
    setWho5Error('');
  };

  const isiResult = isiSeverity(isiTotal);
  const handleOci4Submit = (e) => {
    e.preventDefault();
    if (!allOci4Answered) {
      setOci4Error('Please answer every question before submitting.');
      return;
    }
    setOci4Error('');
    setOci4Submitted(true);
    setTimeout(() => {
      oci4ResultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const handleOci4Reset = () => {
    setOci4(Array(OCI4_QUESTIONS.length).fill(null));
    setOci4Submitted(false);
    setOci4Error('');
  };

  const who5Result = who5Severity(who5Total);
  const handleEpdsSubmit = (e) => {
    e.preventDefault();
    if (!allEpdsAnswered) {
      setEpdsError('Please answer every question before submitting.');
      return;
    }
    setEpdsError('');
    setEpdsSubmitted(true);
    setTimeout(() => {
      epdsResultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const handleEpdsReset = () => {
    setEpds(Array(EPDS_QUESTIONS.length).fill(null));
    setEpdsSubmitted(false);
    setEpdsError('');
  };

  const oci4Result = oci4Severity(oci4Total);
  const epdsResult = epdsSeverity(epdsTotal);
  const epdsSafetyFlag = (epds[9] ?? 0) >= 1;
  const safetyFlag = (phq[8] ?? 0) >= 1;
  const anySubmitted = phqSubmitted || gadSubmitted || auditSubmitted || ftndSubmitted || pssSubmitted || asrsSubmitted || isiSubmitted || mdqSubmitted || who5Submitted || oci4Submitted || epdsSubmitted;
  const currentScaleSubmitted = {
    phq: phqSubmitted, gad: gadSubmitted, audit: auditSubmitted,
    ftnd: ftndSubmitted, pss: pssSubmitted, asrs: asrsSubmitted,
    isi: isiSubmitted, mdq: mdqSubmitted, who5: who5Submitted,
    oci4: oci4Submitted, epds: epdsSubmitted,
  }[activeScale] ?? false;

  return (
    <>
      <Navbar />
      <div className="assess-page">
        <div className="container">

          {activeScale === null ? (
            <>
              <div className="assess-header">
                <span className="section-tag">Self-assessment</span>
                <h1>Mental health &amp; substance use screenings</h1>
                <p>
                  Select a questionnaire to begin. All tools are validated for clinical screening.
                  Answers stay on this device — nothing is sent anywhere. These are screening tools,
                  not a diagnosis.
                </p>
              </div>

              <div className="scale-grid">
                {SCALES.map((s) => (
                  <button
                    key={s.key}
                    className="scale-card"
                    onClick={() => { setActiveScale(s.key); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                  >
                    <span className="scale-card-name">{s.label}</span>
                    <span className="scale-card-subtitle">{s.subtitle}</span>
                    <span className="scale-card-meta">{s.questions} questions · {s.period}</span>
                  </button>
                ))}
              </div>
            </>
          ) : (
            <>
              <div className="assess-nav">
                <button
                  className="btn-back-scale"
                  onClick={() => { setActiveScale(null); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                >
                  ← All screenings
                </button>
              </div>

              <div className="assess-form">

                <section className="assess-card assess-meta">
                  <div className="meta-grid">
                    <label>
                      <span>Date</span>
                      <input
                        type="date"
                        value={meta.date}
                        onChange={(e) => setMeta({ ...meta, date: e.target.value })}
                      />
                    </label>
                    <label>
                      <span>Patient name</span>
                      <input
                        type="text"
                        placeholder="Your name"
                        value={meta.name}
                        onChange={(e) => setMeta({ ...meta, name: e.target.value })}
                      />
                    </label>
                    <label>
                      <span>Date of birth</span>
                      <input
                        type="date"
                        value={meta.dob}
                        onChange={(e) => setMeta({ ...meta, dob: e.target.value })}
                      />
                    </label>
                  </div>
                </section>

                {activeScale === 'phq' && (
                  <form className="assess-card" onSubmit={handlePhqSubmit} noValidate>
                    <header className="assess-card-head">
                      <h2>PHQ-9 — Depression</h2>
                      <p>Over the last 2 weeks, how often have you been bothered by any of the following problems?</p>
                    </header>

                    <div className="scale-list">
                      {PHQ9_QUESTIONS.map((q, i) => (
                        <ScaleRow
                          key={`phq-${i}`}
                          index={i}
                          text={q}
                          name={`phq-${i}`}
                          options={PHQ9_OPTIONS}
                          value={phq[i]}
                          onChange={(v) => setPhqAt(i, v)}
                        />
                      ))}
                    </div>

                    <DifficultyRow
                      name="phq-difficulty"
                      value={phqDifficulty}
                      onChange={setPhqDifficulty}
                    />

                    {phqError && <div className="assess-error" role="alert">{phqError}</div>}

                    <div className="section-actions">
                      <button type="submit" className="btn-primary">
                        {phqSubmitted ? 'Recalculate PHQ-9' : 'See PHQ-9 result'}
                      </button>
                      {phqSubmitted && (
                        <button type="button" className="btn-secondary" onClick={handlePhqReset}>
                          Reset PHQ-9
                        </button>
                      )}
                    </div>

                    {phqSubmitted && (
                      <div className="section-result" ref={phqResultRef} aria-live="polite">
                        {safetyFlag && (
                          <div className="safety-banner" role="alert">
                            <strong>You indicated thoughts of self-harm or suicide.</strong>
                            <p>
                              Please consider reaching out for support right now. India's 24×7 toll-free
                              helpline <b>Tele-MANAS 14416</b> is available, or you can{' '}
                              <Link to="/contact">contact us directly</Link>.
                            </p>
                          </div>
                        )}
                        <PhqResultTile
                          score={phqTotal}
                          band={phqResult.band}
                          tone={phqResult.tone}
                          difficulty={phqDifficulty}
                        />
                      </div>
                    )}
                  </form>
                )}

                {activeScale === 'gad' && (
                  <form className="assess-card" onSubmit={handleGadSubmit} noValidate>
                    <header className="assess-card-head">
                      <h2>GAD-7 — Anxiety</h2>
                      <p>Over the last 2 weeks, how often have you been bothered by the following problems?</p>
                    </header>

                    <div className="scale-list">
                      {GAD7_QUESTIONS.map((q, i) => (
                        <ScaleRow
                          key={`gad-${i}`}
                          index={i}
                          text={q}
                          name={`gad-${i}`}
                          options={GAD7_OPTIONS}
                          value={gad[i]}
                          onChange={(v) => setGadAt(i, v)}
                        />
                      ))}
                    </div>

                    <DifficultyRow
                      name="gad-difficulty"
                      value={gadDifficulty}
                      onChange={setGadDifficulty}
                    />

                    {gadError && <div className="assess-error" role="alert">{gadError}</div>}

                    <div className="section-actions">
                      <button type="submit" className="btn-primary">
                        {gadSubmitted ? 'Recalculate GAD-7' : 'See GAD-7 result'}
                      </button>
                      {gadSubmitted && (
                        <button type="button" className="btn-secondary" onClick={handleGadReset}>
                          Reset GAD-7
                        </button>
                      )}
                    </div>

                    {gadSubmitted && (
                      <div className="section-result" ref={gadResultRef} aria-live="polite">
                        <GadResultTile
                          score={gadTotal}
                          band={gadResult.band}
                          tone={gadResult.tone}
                          difficulty={gadDifficulty}
                        />
                      </div>
                    )}
                  </form>
                )}

                {activeScale === 'audit' && (
                  <form className="assess-card" onSubmit={handleAuditSubmit} noValidate>
                    <header className="assess-card-head">
                      <h2>AUDIT — Alcohol Use</h2>
                      <p>
                        A screening tool to identify harmful patterns of alcohol drinking. In the last one year,
                        how often have the following applied to you?
                      </p>
                      <p className="assess-card-note">
                        <strong>One drink</strong> = 150–250 ml of beer, or 30 ml of whisky / rum / brandy / vodka.
                      </p>
                    </header>

                    <div className="scale-list">
                      {AUDIT_QUESTIONS.map((q, i) => (
                        <ScaleRow
                          key={`audit-${i}`}
                          index={i}
                          text={q.text}
                          name={`audit-${i}`}
                          options={q.options}
                          value={audit[i]}
                          onChange={(v) => setAuditAt(i, v)}
                          optionsClass={q.options.length === 5 ? 'scale-row-options-5' : ''}
                        />
                      ))}
                    </div>

                    <fieldset className="difficulty-row">
                      <legend>Have you ever been in treatment for an alcohol problem?</legend>
                      <div className="difficulty-options">
                        {AUDIT_TREATMENT_OPTIONS.map((opt) => {
                          const id = `audit-treatment-${opt.replace(/\s+/g, '-').toLowerCase()}`;
                          return (
                            <label key={opt} htmlFor={id} className={`difficulty-opt ${auditTreatment === opt ? 'is-selected' : ''}`}>
                              <input
                                id={id}
                                type="radio"
                                name="audit-treatment"
                                value={opt}
                                checked={auditTreatment === opt}
                                onChange={() => setAuditTreatment(opt)}
                              />
                              <span>{opt}</span>
                            </label>
                          );
                        })}
                      </div>
                    </fieldset>

                    {auditError && <div className="assess-error" role="alert">{auditError}</div>}

                    <div className="section-actions">
                      <button type="submit" className="btn-primary">
                        {auditSubmitted ? 'Recalculate AUDIT' : 'See AUDIT result'}
                      </button>
                      {auditSubmitted && (
                        <button type="button" className="btn-secondary" onClick={handleAuditReset}>
                          Reset AUDIT
                        </button>
                      )}
                    </div>

                    {auditSubmitted && (
                      <div className="section-result" ref={auditResultRef} aria-live="polite">
                        <AuditResultTile
                          score={auditTotal}
                          band={auditResult.band}
                          tone={auditResult.tone}
                          treatment={auditTreatment}
                        />
                      </div>
                    )}
                  </form>
                )}

                {activeScale === 'ftnd' && (
                  <form className="assess-card" onSubmit={handleFtndSubmit} noValidate>
                    <header className="assess-card-head">
                      <h2>FTND — Nicotine Dependence</h2>
                      <p>
                        The Fagerstrom Test for Nicotine Dependence helps identify the level of physical
                        dependence on cigarettes and guides appropriate cessation support.
                      </p>
                    </header>

                    <div className="scale-list">
                      {FTND_QUESTIONS.map((q, i) => (
                        <ScaleRow
                          key={`ftnd-${i}`}
                          index={i}
                          text={q.text}
                          name={`ftnd-${i}`}
                          options={q.options}
                          value={ftnd[i]}
                          onChange={(v) => setFtndAt(i, v)}
                        />
                      ))}
                    </div>

                    {ftndError && <div className="assess-error" role="alert">{ftndError}</div>}

                    <div className="section-actions">
                      <button type="submit" className="btn-primary">
                        {ftndSubmitted ? 'Recalculate FTND' : 'See FTND result'}
                      </button>
                      {ftndSubmitted && (
                        <button type="button" className="btn-secondary" onClick={handleFtndReset}>
                          Reset FTND
                        </button>
                      )}
                    </div>

                    {ftndSubmitted && (
                      <div className="section-result" ref={ftndResultRef} aria-live="polite">
                        <FtndResultTile
                          score={ftndTotal}
                          band={ftndResult.band}
                          tone={ftndResult.tone}
                        />
                      </div>
                    )}
                  </form>
                )}

                {activeScale === 'pss' && (
                  <form className="assess-card" onSubmit={handlePssSubmit} noValidate>
                    <header className="assess-card-head">
                      <h2>PSS-10 — Perceived Stress</h2>
                      <p>
                        The Perceived Stress Scale measures how unpredictable, uncontrollable, and
                        overloaded you have found your life over the last month. Some questions are
                        reverse-scored — answer honestly for each one.
                      </p>
                    </header>

                    <div className="scale-list">
                      {PSS_QUESTIONS.map((q, i) => (
                        <ScaleRow
                          key={`pss-${i}`}
                          index={i}
                          text={q.text}
                          name={`pss-${i}`}
                          options={q.options}
                          value={pss[i]}
                          onChange={(v) => setPssAt(i, v)}
                          optionsClass="scale-row-options-5"
                        />
                      ))}
                    </div>

                    {pssError && <div className="assess-error" role="alert">{pssError}</div>}

                    <div className="section-actions">
                      <button type="submit" className="btn-primary">
                        {pssSubmitted ? 'Recalculate PSS-10' : 'See PSS-10 result'}
                      </button>
                      {pssSubmitted && (
                        <button type="button" className="btn-secondary" onClick={handlePssReset}>
                          Reset PSS-10
                        </button>
                      )}
                    </div>

                    {pssSubmitted && (
                      <div className="section-result" ref={pssResultRef} aria-live="polite">
                        <PssResultTile
                          score={pssTotal}
                          band={pssResult.band}
                          tone={pssResult.tone}
                        />
                      </div>
                    )}
                  </form>
                )}

                {activeScale === 'asrs' && (
                  <form className="assess-card" onSubmit={handleAsrsSubmit} noValidate>
                    <header className="assess-card-head">
                      <h2>ASRS — Adult ADHD</h2>
                      <p>
                        Answer based on how you felt and conducted yourself over the <strong>last 6 months</strong>.
                        Part A (Q1–6) is the primary clinical indicator; Part B (Q7–18) provides additional detail.
                      </p>
                    </header>

                    <div className="asrs-section-head">Part A</div>
                    <div className="scale-list">
                      {ASRS_PART_A.map((q, i) => (
                        <ScaleRow
                          key={`asrs-a-${i}`}
                          index={i}
                          text={q}
                          name={`asrs-${i}`}
                          options={ASRS_OPTIONS}
                          value={asrs[i]}
                          onChange={(v) => setAsrsAt(i, v)}
                          optionsClass="scale-row-options-5"
                        />
                      ))}
                    </div>

                    <div className="asrs-section-head">Part B</div>
                    <div className="scale-list">
                      {ASRS_PART_B.map((q, i) => (
                        <ScaleRow
                          key={`asrs-b-${i}`}
                          index={ASRS_PART_A.length + i}
                          text={q}
                          name={`asrs-${ASRS_PART_A.length + i}`}
                          options={ASRS_OPTIONS}
                          value={asrs[ASRS_PART_A.length + i]}
                          onChange={(v) => setAsrsAt(ASRS_PART_A.length + i, v)}
                          optionsClass="scale-row-options-5"
                        />
                      ))}
                    </div>

                    {asrsError && <div className="assess-error" role="alert">{asrsError}</div>}

                    <div className="section-actions">
                      <button type="submit" className="btn-primary">
                        {asrsSubmitted ? 'Recalculate ASRS' : 'See ASRS result'}
                      </button>
                      {asrsSubmitted && (
                        <button type="button" className="btn-secondary" onClick={handleAsrsReset}>
                          Reset ASRS
                        </button>
                      )}
                    </div>

                    {asrsSubmitted && (
                      <div className="section-result" ref={asrsResultRef} aria-live="polite">
                        <AsrsResultTile
                          partAScore={asrsPartATotal}
                          partABand={asrsPartAResult.band}
                          partATone={asrsPartAResult.tone}
                          partBScore={asrsPartBTotal}
                          partBBand={asrsPartBResult.band}
                        />
                      </div>
                    )}
                  </form>
                )}

                {activeScale === 'isi' && (
                  <form className="assess-card" onSubmit={handleIsiSubmit} noValidate>
                    <header className="assess-card-head">
                      <h2>ISI — Insomnia Severity Index</h2>
                      <p>Over the last 2 weeks, please rate the severity of your sleep difficulties.</p>
                    </header>

                    <div className="scale-list">
                      {ISI_QUESTIONS.map((q, i) => (
                        <ScaleRow
                          key={`isi-${i}`}
                          index={i}
                          text={q.text}
                          name={`isi-${i}`}
                          options={q.options}
                          value={isi[i]}
                          onChange={(v) => setIsiAt(i, v)}
                          optionsClass={q.options.length === 5 ? 'scale-row-options-5' : ''}
                        />
                      ))}
                    </div>

                    {isiError && <div className="assess-error" role="alert">{isiError}</div>}

                    <div className="section-actions">
                      <button type="submit" className="btn-primary">
                        {isiSubmitted ? 'Recalculate ISI' : 'See ISI result'}
                      </button>
                      {isiSubmitted && (
                        <button type="button" className="btn-secondary" onClick={handleIsiReset}>
                          Reset ISI
                        </button>
                      )}
                    </div>

                    {isiSubmitted && (
                      <div className="section-result" ref={isiResultRef} aria-live="polite">
                        <IsiResultTile
                          score={isiTotal}
                          band={isiResult.band}
                          tone={isiResult.tone}
                        />
                      </div>
                    )}
                  </form>
                )}

                {activeScale === 'mdq' && (
                  <form className="assess-card" onSubmit={handleMdqSubmit} noValidate>
                    <header className="assess-card-head">
                      <h2>MDQ — Mood Disorder Questionnaire</h2>
                      <p>
                        A screening tool for bipolar spectrum disorders. All three sections must be
                        completed. This is a screening tool — it does not diagnose bipolar disorder.
                      </p>
                    </header>

                    <div className="mdq-q1-head">
                      <span className="scale-row-num">1.</span> Have you ever had a period of time when you were not your usual self and…
                    </div>
                    <div className="scale-list">
                      {MDQ_ITEMS.map((item, i) => (
                        <MdqBinaryRow
                          key={`mdq-item-${i}`}
                          label={String.fromCharCode(97 + i)}
                          text={item}
                          name={`mdq-item-${i}`}
                          value={mdqItems[i]}
                          onChange={(v) => setMdqItemAt(i, v)}
                        />
                      ))}
                    </div>

                    <MdqBinaryRow
                      label="2"
                      text="Did several of these happen during the same period of time?"
                      name="mdq-q2"
                      value={mdqQ2}
                      onChange={setMdqQ2}
                    />

                    <fieldset className="difficulty-row">
                      <legend>
                        <span className="scale-row-num">3.</span> How much of a problem did any of these cause you — such as being unable to work, having family, money, or legal troubles, or getting into arguments or fights?
                      </legend>
                      <div className="difficulty-options">
                        {MDQ_Q3_OPTIONS.map((opt) => {
                          const id = `mdq-q3-${opt.replace(/\s+/g, '-').toLowerCase()}`;
                          return (
                            <label key={opt} htmlFor={id} className={`difficulty-opt ${mdqQ3 === opt ? 'is-selected' : ''}`}>
                              <input
                                id={id}
                                type="radio"
                                name="mdq-q3"
                                value={opt}
                                checked={mdqQ3 === opt}
                                onChange={() => setMdqQ3(opt)}
                              />
                              <span>{opt}</span>
                            </label>
                          );
                        })}
                      </div>
                    </fieldset>

                    {mdqError && <div className="assess-error" role="alert">{mdqError}</div>}

                    <div className="section-actions">
                      <button type="submit" className="btn-primary">
                        {mdqSubmitted ? 'Recalculate MDQ' : 'See MDQ result'}
                      </button>
                      {mdqSubmitted && (
                        <button type="button" className="btn-secondary" onClick={handleMdqReset}>
                          Reset MDQ
                        </button>
                      )}
                    </div>

                    {mdqSubmitted && (
                      <div className="section-result" ref={mdqResultRef} aria-live="polite">
                        <MdqResultTile
                          itemScore={mdqScore}
                          q2={mdqQ2}
                          q3={mdqQ3}
                        />
                      </div>
                    )}
                  </form>
                )}

                {activeScale === 'who5' && (
                  <form className="assess-card" onSubmit={handleWho5Submit} noValidate>
                    <header className="assess-card-head">
                      <h2>WHO-5 — Well-being Index</h2>
                      <p>
                        Over the last 2 weeks, please rate how often each statement applied to you.
                        Higher scores indicate better quality of life (0 = worst, 25 = best possible).
                      </p>
                    </header>

                    <div className="scale-list">
                      {WHO5_QUESTIONS.map((q, i) => (
                        <ScaleRow
                          key={`who5-${i}`}
                          index={i}
                          text={q}
                          name={`who5-${i}`}
                          options={WHO5_OPTIONS}
                          value={who5[i]}
                          onChange={(v) => setWho5At(i, v)}
                          optionsClass="scale-row-options-6"
                        />
                      ))}
                    </div>

                    {who5Error && <div className="assess-error" role="alert">{who5Error}</div>}

                    <div className="section-actions">
                      <button type="submit" className="btn-primary">
                        {who5Submitted ? 'Recalculate WHO-5' : 'See WHO-5 result'}
                      </button>
                      {who5Submitted && (
                        <button type="button" className="btn-secondary" onClick={handleWho5Reset}>
                          Reset WHO-5
                        </button>
                      )}
                    </div>

                    {who5Submitted && (
                      <div className="section-result" ref={who5ResultRef} aria-live="polite">
                        <Who5ResultTile
                          score={who5Total}
                          band={who5Result.band}
                          tone={who5Result.tone}
                        />
                      </div>
                    )}
                  </form>
                )}

                {activeScale === 'oci4' && (
                  <form className="assess-card" onSubmit={handleOci4Submit} noValidate>
                    <header className="assess-card-head">
                      <h2>OCI-4 — Obsessive Compulsive Inventory</h2>
                      <p>
                        The following statements refer to experiences many people have in everyday life.
                        Rate how much each experience has distressed or bothered you during the <strong>past month</strong>.
                      </p>
                    </header>

                    <div className="scale-list">
                      {OCI4_QUESTIONS.map((q, i) => (
                        <ScaleRow
                          key={`oci4-${i}`}
                          index={i}
                          text={q}
                          name={`oci4-${i}`}
                          options={OCI4_OPTIONS}
                          value={oci4[i]}
                          onChange={(v) => setOci4At(i, v)}
                          optionsClass="scale-row-options-5"
                        />
                      ))}
                    </div>

                    {oci4Error && <div className="assess-error" role="alert">{oci4Error}</div>}

                    <div className="section-actions">
                      <button type="submit" className="btn-primary">
                        {oci4Submitted ? 'Recalculate OCI-4' : 'See OCI-4 result'}
                      </button>
                      {oci4Submitted && (
                        <button type="button" className="btn-secondary" onClick={handleOci4Reset}>
                          Reset OCI-4
                        </button>
                      )}
                    </div>

                    {oci4Submitted && (
                      <div className="section-result" ref={oci4ResultRef} aria-live="polite">
                        <Oci4ResultTile
                          score={oci4Total}
                          band={oci4Result.band}
                          tone={oci4Result.tone}
                        />
                      </div>
                    )}
                  </form>
                )}

                {activeScale === 'epds' && (
                  <form className="assess-card" onSubmit={handleEpdsSubmit} noValidate>
                    <header className="assess-card-head">
                      <h2>EPDS — Postnatal Depression</h2>
                      <p>
                        For pregnant women or those who have recently had a baby. Please answer based
                        on how you have felt in the <strong>last 7 days</strong>.
                      </p>
                    </header>

                    <div className="scale-list">
                      {EPDS_QUESTIONS.map((q, i) => (
                        <ScaleRow
                          key={`epds-${i}`}
                          index={i}
                          text={q.text}
                          name={`epds-${i}`}
                          options={q.options}
                          value={epds[i]}
                          onChange={(v) => setEpdsAt(i, v)}
                        />
                      ))}
                    </div>

                    {epdsError && <div className="assess-error" role="alert">{epdsError}</div>}

                    <div className="section-actions">
                      <button type="submit" className="btn-primary">
                        {epdsSubmitted ? 'Recalculate EPDS' : 'See EPDS result'}
                      </button>
                      {epdsSubmitted && (
                        <button type="button" className="btn-secondary" onClick={handleEpdsReset}>
                          Reset EPDS
                        </button>
                      )}
                    </div>

                    {epdsSubmitted && (
                      <div className="section-result" ref={epdsResultRef} aria-live="polite">
                        <EpdsResultTile
                          score={epdsTotal}
                          band={epdsResult.band}
                          tone={epdsResult.tone}
                          safetyFlag={epdsSafetyFlag}
                        />
                      </div>
                    )}
                  </form>
                )}

              </div>

              {currentScaleSubmitted && (
                <div className="assess-cta">
                  <div className="assess-cta-text">
                    <span className="assess-cta-tag">Next step</span>
                    <h3>Talk to someone who can help</h3>
                    <p>
                      These scores aren't a diagnosis — but they're a strong starting point for
                      a conversation with a clinician about what you're experiencing.
                    </p>
                  </div>
                  <a
                    href="https://book.healthplix.com/dr-doctor-s-name-specialisation-place-city_198213"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary"
                  >
                    Book a session
                  </a>
                </div>
              )}

              <div className="assess-disclaimer">
                <p>
                  <strong>Educational use only.</strong> The PHQ-9 and GAD-7 are validated screening
                  tools, but they don't replace evaluation by a qualified mental health professional.
                  In a crisis, call <b>Tele-MANAS 14416</b> (toll-free, 24×7).
                </p>
                <p className="assess-attribution">
                  Developed by Drs. Robert L. Spitzer, Janet B.W. Williams, Kurt Kroenke and colleagues,
                  with an educational grant from Pfizer Inc. No permission required to reproduce.
                </p>
              </div>
            </>
          )}

        </div>
      </div>
      <Footer />
    </>
  );
};

export default SelfAssessment;
